import { MergeSuppliersModal } from '@/features/suppliers/MergeSuppliersModal';
import { useAuth } from '@/context/AuthContext';
import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSuppliers } from '@/services/suppliers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Supplier } from '@/types/models';
import { TableSkeleton } from '@/components/common/Skeleton';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { Modal } from '@/components/common/Modal';
import { SupplierForm } from '@/features/suppliers/SupplierForm';
import { SupplierDetail } from '@/features/suppliers/SupplierDetail';
import { Building2, Plus, Phone, Mail, User } from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

export const SuppliersPage: React.FC = () => {
  const { role } = useAuth();
  const isOwner = role === 'owner';
  const [showMerge, setShowMerge] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    setPage(0);
    const timer = setTimeout(() => setDebouncedSearch(val), 400);
    return () => clearTimeout(timer);
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.suppliers.list({ search: debouncedSearch, page }),
    queryFn: () => fetchSuppliers({ search: debouncedSearch, page }),
    placeholderData: (prev) => prev,
  });

  const suppliers = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-brand-black flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-brand-green" />
            <span>Suppliers</span>
          </h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {totalCount > 0 ? `${totalCount} suppliers total` : 'Manage your supplier directory'}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm shrink-0"
          id="add-supplier-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Add Supplier</span>
        </button>
      </div>

      {isOwner && <button type="button" onClick={() => setShowMerge(true)} className="px-3 py-2 border border-brand-border rounded text-sm font-semibold">Merge duplicate suppliers</button>}
      {showMerge && isOwner && <MergeSuppliersModal onClose={() => setShowMerge(false)} />}

      {/* Search */}
      <div className="flex items-center space-x-3">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by name, contact or email..."
        />
      </div>

      {/* Error */}
      {error && (
        <ErrorAlert
          message={parseDatabaseError(error)}
          onRetry={() => refetch()}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-brand-border shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-bg border-b border-brand-border">
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider">
                  Supplier Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden sm:table-cell">
                  Contact Person
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden md:table-cell">
                  Phone
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-brand-muted uppercase tracking-wider hidden lg:table-cell">
                  Email
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>

            {isLoading ? (
              <TableSkeleton rows={8} cols={5} />
            ) : suppliers.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={5}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mb-4">
                        <Building2 className="w-8 h-8 text-brand-green" />
                      </div>
                      <p className="text-base font-bold text-brand-black">
                        {debouncedSearch ? 'No suppliers found' : 'No suppliers yet'}
                      </p>
                      <p className="text-xs text-brand-muted mt-1 max-w-xs">
                        {debouncedSearch
                          ? `No results for "${debouncedSearch}". Try a different search term.`
                          : 'Add your first supplier to get started.'}
                      </p>
                      {!debouncedSearch && (
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add First Supplier</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-brand-border">
                {suppliers.map((supplier: Supplier) => (
                  <tr
                    key={supplier.id}
                    onClick={() => setSelectedSupplierId(supplier.id)}
                    className="hover:bg-brand-bg cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-brand-green text-white flex items-center justify-center text-xs font-bold uppercase shrink-0">
                          {supplier.name.charAt(0)}
                        </div>
                        <span className="font-medium text-brand-black">{supplier.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted hidden sm:table-cell">
                      {supplier.contact_name ? (
                        <span className="inline-flex items-center space-x-1">
                          <User className="w-3 h-3" />
                          <span>{supplier.contact_name}</span>
                        </span>
                      ) : (
                        <span className="text-brand-border">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted hidden md:table-cell">
                      {supplier.phone ? (
                        <span className="inline-flex items-center space-x-1">
                          <Phone className="w-3 h-3" />
                          <span>{supplier.phone}</span>
                        </span>
                      ) : (
                        <span className="text-brand-border">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted hidden lg:table-cell">
                      {supplier.email ? (
                        <span className="inline-flex items-center space-x-1">
                          <Mail className="w-3 h-3" />
                          <span className="truncate max-w-[180px]">{supplier.email}</span>
                        </span>
                      ) : (
                        <span className="text-brand-border">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs text-brand-green font-semibold hover:underline">
                        View →
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        <Pagination
          page={page}
          totalCount={totalCount}
          onPageChange={(p) => setPage(p)}
          pageSize={PAGE_SIZE}
        />
      </div>

      {/* Add Supplier Modal */}
      {showAddModal && (
        <Modal isOpen title="Add Supplier" onClose={() => setShowAddModal(false)}>
          <SupplierForm
            onSuccess={() => setShowAddModal(false)}
            onCancel={() => setShowAddModal(false)}
          />
        </Modal>
      )}

      {/* Supplier Detail Modal */}
      {selectedSupplierId && (
        <Modal
          isOpen
          title="Supplier Details"
          onClose={() => setSelectedSupplierId(null)}
          maxWidth="lg"
        >
          <SupplierDetail
            supplierId={selectedSupplierId}
            onClose={() => setSelectedSupplierId(null)}
          />
        </Modal>
      )}
    </div>
  );
};
